import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Palette, Ruler, Upload, Monitor, Moon, Wrench, Image } from 'lucide-react';
import { getMeasurementDescription, getMeasurementExample } from '../utils/measurements';
import { getBackendUrl, getApiUrl } from '../utils/config';

const BACKEND_URL = getBackendUrl();
const API = getApiUrl();

const Settings = ({ user, trigger, onSettingsChange }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    theme: 'default',
    measurement_system: 'metric',
    custom_background: null
  });
  const [customBackgroundFile, setCustomBackgroundFile] = useState(null);

  useEffect(() => {
    if (open) {
      fetchPreferences();
    }
  }, [open]);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/user/preferences`);
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
      // Use defaults if preferences don't exist yet
    }
  };

  const handlePreferenceChange = async (key, value) => {
    try {
      setLoading(true);
      
      const updateData = { [key]: value };
      
      if (key === 'custom_background' && customBackgroundFile) {
        // Handle file upload
        const reader = new FileReader();
        reader.onload = async (e) => {
          const base64Data = e.target.result.split(',')[1];
          updateData.custom_background = {
            data: base64Data,
            content_type: customBackgroundFile.type,
            filename: customBackgroundFile.name
          };
          
          await updatePreferences(updateData);
        };
        reader.readAsDataURL(customBackgroundFile);
      } else {
        await updatePreferences(updateData);
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (updateData) => {
    try {
      const response = await axios.put(`${API}/user/preferences`, updateData);
      setPreferences(response.data);
      
      // Notify parent component about settings change
      if (onSettingsChange) {
        onSettingsChange(response.data);
      }
      
      toast.success('Settings updated successfully!');
    } catch (error) {
      throw error;
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      
      setCustomBackgroundFile(file);
    }
  };

  const uploadCustomBackground = async () => {
    if (customBackgroundFile) {
      await handlePreferenceChange('custom_background', true);
      setCustomBackgroundFile(null);
      // Clear the file input
      document.getElementById('background-upload').value = '';
    }
  };

  const getThemeIcon = (theme) => {
    switch (theme) {
      case 'dark': return <Moon className="w-5 h-5" />;
      case 'garage': return <Wrench className="w-5 h-5" />;
      default: return <Monitor className="w-5 h-5" />;
    }
  };

  const getThemePreview = (theme) => {
    switch (theme) {
      case 'default':
        return (
          <div className="w-full h-16 bg-gradient-to-br from-gray-50 to-gray-100 border rounded flex items-center justify-center">
            <span className="text-sm text-gray-600">Clean & Modern</span>
          </div>
        );
      case 'dark':
        return (
          <div className="w-full h-16 bg-gradient-to-br from-gray-800 to-gray-900 border rounded flex items-center justify-center">
            <span className="text-sm text-gray-300">Dark Theme</span>
          </div>
        );
      case 'garage':
        return (
          <div className="w-full h-16 border rounded flex items-center justify-center relative overflow-hidden bg-cover bg-center" 
               style={{
                 backgroundImage: 'url("https://customer-assets.emergentagent.com/job_maintenance-pal-1/artifacts/8lszdnzs_ChatGPT%20Image%20Sep%2025%2C%202025%2C%2008_56_15%20PM.png")',
                 backgroundSize: 'cover',
                 backgroundPosition: 'center'
               }}>
            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
            <span className="text-sm text-white relative z-10 font-medium">Workshop</span>
          </div>
        );
      default:
        return null;
    }
  };

  const defaultTrigger = (
    <Button variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-50">
      <SettingsIcon className="w-4 h-4 mr-2" />
      Settings
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <SettingsIcon className="w-5 h-5 mr-2 text-blue-600" />
            App Settings
          </DialogTitle>
          <DialogDescription>
            Customize your Track My Garage experience
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Theme Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Palette className="w-5 h-5 mr-2 text-purple-600" />
                Appearance
              </CardTitle>
              <CardDescription>
                Choose your preferred theme and background
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Theme Selection */}
              <div className="space-y-3">
                <Label>Theme</Label>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { value: 'default', label: 'Default', desc: 'Clean and modern light theme' },
                    { value: 'dark', label: 'Dark Mode', desc: 'Easy on the eyes for low-light use' },
                    { value: 'garage', label: 'Garage Workshop', desc: 'Automotive-themed workspace feel' }
                  ].map((theme) => (
                    <div
                      key={theme.value}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        preferences.theme === theme.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handlePreferenceChange('theme', theme.value)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          {getThemeIcon(theme.value)}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{theme.label}</div>
                          <div className="text-sm text-gray-500">{theme.desc}</div>
                        </div>
                        <div className="w-20">
                          {getThemePreview(theme.value)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Background Upload */}
              <div className="space-y-3 pt-4 border-t">
                <Label className="flex items-center">
                  <Image className="w-4 h-4 mr-2" />
                  Custom Background
                </Label>
                <p className="text-sm text-gray-500">
                  Upload your own background image (max 5MB)
                </p>
                
                <div className="flex items-center space-x-3">
                  <Input
                    id="background-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="flex-1"
                  />
                  {customBackgroundFile && (
                    <Button
                      onClick={uploadCustomBackground}
                      disabled={loading}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {loading ? 'Uploading...' : 'Upload'}
                    </Button>
                  )}
                </div>
                
                {preferences.custom_background && (
                  <div className="mt-2">
                    <div className="text-sm text-green-600">
                      ✓ Custom background uploaded
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePreferenceChange('theme', 'custom')}
                      className="mt-2"
                    >
                      Use Custom Background
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Measurement Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Ruler className="w-5 h-5 mr-2 text-green-600" />
                Measurements
              </CardTitle>
              <CardDescription>
                Choose your preferred measurement system
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Label>Measurement System</Label>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { 
                      value: 'metric', 
                      label: 'Metric System', 
                      desc: getMeasurementDescription('metric'),
                      example: getMeasurementExample('metric')
                    },
                    { 
                      value: 'imperial', 
                      label: 'Imperial System', 
                      desc: getMeasurementDescription('imperial'),
                      example: getMeasurementExample('imperial')
                    }
                  ].map((system) => (
                    <div
                      key={system.value}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        preferences.measurement_system === system.value
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handlePreferenceChange('measurement_system', system.value)}
                    >
                      <div className="font-medium">{system.label}</div>
                      <div className="text-sm text-gray-500 mt-1">{system.desc}</div>
                      <div className="text-xs text-gray-400 mt-1">{system.example}</div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Account Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium">{user?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium">{user?.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-medium">{user?.subscription?.plan_name || 'Free'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Role:</span>
                  <span className="font-medium capitalize">{user?.role || 'User'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Close Button */}
          <div className="flex justify-end pt-4">
            <Button onClick={() => setOpen(false)} className="bg-blue-600 hover:bg-blue-700">
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Settings;